import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const DEFAULT_FILE_NAME = "history_log.xlsx";

/**
 * Result of requesting the link change history report.
 *
 * - url: Piper generated the report; `url` is a temporary signed link.
 * - file: legacy report, already generated as a file.
 * - error: the backend could not generate the report (e.g. 502 from Piper).
 * - network: the request failed before a response (network, CORS or expired Darwin session).
 */
export type LinkLogReportResult =
  | { type: "url"; url: string }
  | { type: "file"; blob: Blob; fileName: string }
  | { type: "error" }
  | { type: "network" };

/**
 * Converts a local "YYYY-MM-DD HH:mm:ss" date range to UTC with the same format.
 *
 * @param dateRange - Local date range from the date range selector
 * @returns The same range in UTC, or undefined when there is no range
 */
export const toUtcDateRange = (dateRange?: { start: string; end: string }) => {
  if (!dateRange) {
    return undefined;
  }

  const toUtc = (date: string) => dayjs(date).utc().format("YYYY-MM-DD HH:mm:ss");

  return { start: toUtc(dateRange.start), end: toUtc(dateRange.end) };
};

/**
 * Decodes a percent-encoded value, keeping it as is when it is not valid encoding.
 *
 * @param value - Raw value from the header, e.g. `report%20links.xlsx` or `100%.xlsx`
 * @returns The decoded value, or the original one when it cannot be decoded
 */
const safeDecodeURIComponent = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/**
 * Extracts the file name from a Content-Disposition header.
 *
 * @param contentDisposition - Header value, e.g. `attachment; filename="report.xlsx"`
 * @returns The file name, or a default name when the header has none
 */
export const getFileNameFromContentDisposition = (contentDisposition: string | null) => {
  const match = contentDisposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);

  return match ? safeDecodeURIComponent(match[1]) : DEFAULT_FILE_NAME;
};

/**
 * Requests the link change history report with the Darwin session cookie.
 *
 * The backend decides between Piper (JSON `{url}`) and the legacy report (file),
 * so the result type depends only on the response. Reading the file body can
 * fail after the response started (e.g. connection lost mid-download), which is
 * reported as an error.
 *
 * @param reportUrl - Full report URL, including the `filter` query param
 * @returns The report result; the promise never rejects
 */
export const requestLinkLogReport = async (reportUrl: string): Promise<LinkLogReportResult> => {
  let response: Response;

  try {
    response = await fetch(reportUrl, { credentials: "include" });
  } catch {
    return { type: "network" };
  }

  if (!response.ok) {
    return { type: "error" };
  }

  if (response.headers.get("content-type")?.includes("application/json")) {
    const body = await response.json().catch(() => null);

    return body?.url ? { type: "url", url: body.url } : { type: "error" };
  }

  const blob = await response.blob().catch(() => null);

  if (!blob) {
    return { type: "error" };
  }

  return {
    type: "file",
    blob,
    fileName: getFileNameFromContentDisposition(response.headers.get("content-disposition")),
  };
};
